// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import Decimal from 'decimal.js';
import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import { digitalMediaIdStr } from './digitalMedia';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  // In development, the dev API server is running in a different port
  if (useDevApiServer) {
    // If we are in the browser and accessing via ngrok (or any non-localhost origin),
    // use relative path so the request goes through the dev server proxy.
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
      return '';
    }
    return `http://localhost:${port}`;
  }

  const sanitizeUrl = url => (url ? url.replace(/\/$/, '') : '');

  if (typeof window !== 'undefined' && window.location) {
    return sanitizeUrl(marketplaceRootURL) || window.location.origin;
  }

  const fallbackUrl =
    sanitizeUrl(marketplaceRootURL) ||
    sanitizeUrl(process.env.REACT_APP_MARKETPLACE_ROOT_URL) ||
    sanitizeUrl(process.env.MARKETPLACE_ROOT_URL);

  if (!fallbackUrl) {
    console.warn(
      'apiBaseUrl: No base URL available during SSR. Please set REACT_APP_MARKETPLACE_ROOT_URL environment variable.'
    );
    return ''; // Return empty string to cause a clear error rather than undefined behavior
  }

  return fallbackUrl;
};

// Application type handlers for JS SDK.
//
// NOTE: keep in sync with `typeHandlers` in `server/api-util/sdk.js`
export const typeHandlers = [
  // Use Decimal type instead of SDK's BigDecimal.
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

// If server/api returns data from SDK, you should set Content-Type to 'application/transit+json'
const request = (path, options = {}) => {
  const baseUrl = apiBaseUrl();
  // If we are server-side, we must have a base URL.
  // Client-side, an empty string is fine (it implies relative path).
  if (!baseUrl && typeof window === 'undefined') {
    return Promise.reject(
      new Error(
        'API base URL not available. Cannot make request during SSR without proper configuration.'
      )
    );
  }

  // In the browser, call same-origin `/api/...` so the CRA proxy forwards
  // auth cookies. Hitting localhost:3500 directly often sends only a
  // public-read token and auth endpoints then fail with 403.
  const url =
    typeof window !== 'undefined' && path.startsWith('/api/') ? path : `${baseUrl}${path}`;
  const { credentials, headers, body, ...rest } = options;

  // If headers are not set, we assume that the body should be serialized as transit format.
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};
  const jsonBodyMaybe =
    headers && headers['Content-Type'] === 'application/json' && body ? { body } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    // Since server/api mostly talks to Marketplace API using SDK,
    // we default to 'application/transit+json' as content type (as SDK uses transit).
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...jsonBodyMaybe,
    ...rest,
  };

  // Universal fetch that works in both browser and Node.js (18+) environments
  const universalFetch = typeof window !== 'undefined' ? window.fetch : fetch;

  return universalFetch(url, fetchOptions)
    .then(res => {
      const contentTypeHeader = res.headers.get('Content-Type');
      const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;

      if (res.status >= 400) {
        return res.json().then(data => {
          let e = new Error();
          e = Object.assign(e, data);

          throw e;
        });
      }
      if (contentType === 'application/transit+json') {
        return res.text().then(deserialize);
      } else if (contentType === 'application/json') {
        return res.json();
      }
      return res.text();
    })
    .catch(error => {
      // Enhanced error logging for debugging SSR issues
      console.error('API request failed:', {
        url,
        error: error.message,
        isSSR: typeof window === 'undefined',
        apiBaseUrl: apiBaseUrl(),
      });
      throw error;
    });
};

// Keep the previous parameter order for the post method.
// For now, only POST has own specific function, but you can create more or use request directly.
const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };

  return request(path, requestOptions);
};

/**
 * URL for browser-originated API calls. Uses a same-origin `/api/...` path so the CRA dev
 * server proxy (setupProxy) forwards to the API server; avoids CORS/cookie issues when
 * `apiBaseUrl()` points at a different host/port than the page (e.g. localhost:3500 vs 127.0.0.1:3000).
 */
const browserApiPath = path => {
  if (typeof window !== 'undefined') {
    return path.startsWith('/') ? path : `/${path}`;
  }
  const baseUrl = apiBaseUrl();
  if (!baseUrl) {
    return null;
  }
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl.replace(/\/$/, '')}${p}`;
};

const jsonFetch = (path, options = {}) => {
  const universalFetch = typeof window !== 'undefined' ? window.fetch : fetch;
  let url;
  if (typeof window !== 'undefined' && path.startsWith('/api/')) {
    /** Same-origin /api so CRA proxy forwards cookies; avoids cross-port localhost cookie issues. */
    url = browserApiPath(path);
  } else {
    const baseUrl = apiBaseUrl();
    if (!baseUrl && typeof window === 'undefined') {
      return Promise.reject(new Error('API base URL not available for SSR'));
    }
    url = `${baseUrl}${path}`;
  }
  return universalFetch(url, { credentials: 'include', ...options })
    .then(async res => {
      const ct = res.headers.get('Content-Type') || '';
      let data = null;
      if (ct.includes('application/json')) {
        const text = await res.text();
        data = text ? JSON.parse(text) : null;
      } else {
        data = await res.text();
      }
      if (!res.ok) {
        const err = new Error(
          (data && typeof data === 'object' && (data.error || data.message)) ||
            (typeof data === 'string' && data) ||
            res.statusText
        );
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data;
    })
    .catch(error => {
      // Normalize browser network failures (API down / proxy ECONNREFUSED) so callers
      // can show a specific toast instead of the generic messaging-auth message.
      if (error && error.status == null) {
        error.status = 0;
        error.code = error.code || 'network_error';
      }
      throw error;
    });
};

const toIsoQueryParam = value => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toISOString();
  }
  if (typeof value?.toISOString === 'function') {
    return value.toISOString();
  }
  return null;
};

// Fetch transaction line items from the local API endpoint.
//
// See `server/api/transaction-line-items.js` to see what data should
// be sent in the body.
export const transactionLineItems = body => {
  return post('/api/transaction-line-items', body);
};

/**
 * Sold counts for listing price tiers (seeker Sold-out badges).
 * GET /api/listings/:listingId/price-tier-units
 */
const priceTierSlotQuery = value => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString();
  }
  if (typeof value === 'object' && value.date) {
    return priceTierSlotQuery(value.date);
  }
  return typeof value === 'string' && value ? value : null;
};

export const fetchPriceTierUnits = (listingId, slotStart) => {
  const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
  if (!id) {
    return Promise.resolve({ unitsSoldByTierId: {} });
  }
  const start = priceTierSlotQuery(slotStart);
  const query = start ? `?slotStart=${encodeURIComponent(start)}` : '';
  return jsonFetch(`/api/listings/${id}/price-tier-units${query}`, { method: 'GET' }).then(
    data => ({
      unitsSoldByTierId: data?.unitsSoldByTierId || {},
    })
  );
};

export const PRICE_TIER_UNITS_RETRY_COUNT = 2;
export const PRICE_TIER_UNITS_RETRY_BACKOFF_MS = [300, 800];

const defaultDelay = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Retry a failed price-tier-units fetch so a single blip does not look
 * like "nothing sold". Callers must not treat a thrown error as {}.
 */
export const fetchPriceTierUnitsWithRetry = (listingId, slotStart, options = {}) => {
  const {
    retries = PRICE_TIER_UNITS_RETRY_COUNT,
    backoffMs = PRICE_TIER_UNITS_RETRY_BACKOFF_MS,
    delay = defaultDelay,
    fetchFn = fetchPriceTierUnits,
  } = options;

  const run = async () => {
    let lastError;
    const attempts = retries + 1;
    for (let attempt = 0; attempt < attempts; attempt++) {
      try {
        return await fetchFn(listingId, slotStart);
      } catch (error) {
        lastError = error;
        if (attempt < attempts - 1) {
          await delay(backoffMs[attempt] || backoffMs[backoffMs.length - 1] || 0);
        }
      }
    }
    throw lastError;
  };

  return run();
};

// Initiate a privileged transaction.
//
// With privileged transitions, the transactions need to be created
// from the backend. This endpoint enables sending the order data to
// the local backend, and passing that to the Marketplace API.
//
// See `server/api/initiate-privileged.js` to see what data should be
// sent in the body.
export const initiatePrivileged = body => {
  return post('/api/initiate-privileged', body);
};

export const validateDiscountApi = body => {
  return post('/api/validate-discount', body);
};

// Transition a transaction with a privileged transition.
//
// This is similar to the `initiatePrivileged` above. It will use the
// backend for the transition. The backend endpoint will add the
// payment line items to the transition params.
//
// See `server/api/transition-privileged.js` to see what data should
// be sent in the body.
export const transitionPrivileged = body => {
  return post('/api/transition-privileged', body);
};

// Create user with identity provider (e.g. Facebook or Google)
//
// If loginWithIdp api call fails and user can't authenticate to Marketplace API with idp
// we will show option to create a new user with idp.
// For that user needs to confirm data fetched from the idp.
// After the confirmation, this endpoint is called to create a new user with confirmed data.
//
// See `server/api/auth/createUserWithIdp.js` to see what data should
// be sent in the body.
export const createUserWithIdp = body => {
  return post('/api/auth/create-user-with-idp', body);
};

export const getCommissionInfo = body => {
  return post('/api/commission-info', body);
};

/** Notify operators that alternative payout details were saved/updated. */
export const notifyAlternativePayoutApi = body => {
  return post('/api/alternative-payout-notify', body);
};

// Get the first available time slot for a listing via GET.
// Endpoint: GET /api/listings/:listingId/timeslots/first
// Returns transit/json: { data: timeSlot | null }
export const firstTimeslot = ({ listingId, timeslotStart, timeslotEnd }) => {
  const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
  if (!id) return Promise.reject(new Error('listingId is required'));
  const params = new URLSearchParams();
  if (timeslotStart) params.append('timeslotStart', timeslotStart);
  if (timeslotEnd) params.append('timeslotEnd', timeslotEnd);
  const query = params.toString();
  const url = `/api/listings/${id}/timeslots/first${query ? `?${query}` : ''}`;
  return request(url, { method: methods.GET });
};

// Get pending review items for the currently authenticated user.
// Endpoint: GET /api/pending-reviews
// Returns transit/json: { data: Array<PendingReviewItem> }
export const pendingReviews = () => {
  return request('/api/pending-reviews', { method: methods.GET });
};

/**
 * Mark conversation read for cross-party ticks (Integration API metadata).
 * Unread badges still use Marketplace privateData.messageReadAt.
 */
export const markConversationReadApi = transactionId =>
  jsonFetch('/api/mark-conversation-read', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transactionId }),
  });

/** Fetch peer read-receipt map from Integration API metadata (via local API). */
export const fetchConversationReadStatusApi = transactionId => {
  const params = new URLSearchParams({ transactionId });
  return jsonFetch(`/api/conversation-read-status?${params.toString()}`, {
    method: methods.GET,
  });
};

// Saved listings (Save for later)
export const fetchSavedListingsApi = () =>
  jsonFetch('/api/saved-listings', { method: methods.GET });
export const saveListingApi = (listingId, timeslotId, timeslotStart, timeslotEnd) =>
  jsonFetch('/api/saved-listings', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      listingId,
      timeslotId: timeslotId || undefined,
      timeslotStart: timeslotStart || undefined,
      timeslotEnd: timeslotEnd || undefined,
    }),
  });
export const unsaveListingApi = (listingId, timeslotId, timeslotStart, timeslotEnd) => {
  const params = new URLSearchParams();
  if (timeslotId) {
    params.set('timeslotId', timeslotId);
  }
  const startIso = toIsoQueryParam(timeslotStart);
  const endIso = toIsoQueryParam(timeslotEnd);
  if (startIso) {
    params.set('timeslotStart', startIso);
  }
  if (endIso) {
    params.set('timeslotEnd', endIso);
  }
  const qs = params.toString();
  const url = qs
    ? `/api/saved-listings/${encodeURIComponent(listingId)}?${qs}`
    : `/api/saved-listings/${encodeURIComponent(listingId)}`;
  return jsonFetch(url, { method: methods.DELETE });
};

export const fetchCampaignRecipientsApi = () =>
  jsonFetch('/api/campaigns/recipients', { method: methods.GET });

export const sendCampaignApi = ({ subject, body, recipientIds }) =>
  jsonFetch('/api/campaigns', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subject, body, recipientIds }),
  });

export const fetchCampaignApi = campaignId =>
  jsonFetch(`/api/campaigns/${encodeURIComponent(campaignId)}`, { method: methods.GET });

export const fetchCampaignEmailPreferenceApi = practitionerId => {
  const params = new URLSearchParams({ practitionerId });
  return jsonFetch(`/api/campaigns/email-preference?${params.toString()}`, {
    method: methods.GET,
  });
};

export const setCampaignEmailPreferenceApi = ({ practitionerId, muted }) =>
  jsonFetch('/api/campaigns/email-preference', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ practitionerId, muted }),
  });

export const captureProviderMessagingCredential = () =>
  jsonFetch('/api/provider-messaging-credentials', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });

/** Triggers post-booking welcome message after client-side confirm-payment or accept. */
export const triggerPostBookingWelcomeMessage = (transactionId, options = {}) => {
  const id = transactionId?.uuid || transactionId;
  return jsonFetch('/api/post-booking-welcome-message', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    // The server acks 202 before sending, so the request completes well before
    // navigation and does not need fetch keepalive.
    body: JSON.stringify({
      transactionId: id,
      delayMs: options.delayMs || 0,
      retryOnSkip: !!options.retryOnSkip,
      ...(options.retryDelayMs != null ? { retryDelayMs: options.retryDelayMs } : {}),
    }),
  });
};

/** Must match dev proxy + server limits; Fast.io chunked flow can poll up to ~15 min. */
const DIGITAL_MEDIA_UPLOAD_XHR_TIMEOUT_MS = 20 * 60 * 1000;
/** Per-chunk XHR timeout (large slices on slow uplinks). */
const DIGITAL_MEDIA_CHUNK_XHR_TIMEOUT_MS = 10 * 60 * 1000;
/** Fast.io single-call max — files at or below use legacy one-shot upload (docs.fast.io/upload.html). */
const FASTIO_SINGLE_CALL_MAX_BYTES = 4 * 1024 * 1024;

const digitalMediaUploadClientDebug = () =>
  typeof window !== 'undefined' &&
  (window.localStorage?.getItem('DEBUG_DIGITAL_MEDIA') === '1' ||
    (typeof process !== 'undefined' &&
      process.env &&
      process.env.REACT_APP_FASTIO_DEBUG === 'true'));

const xhrPostFormData = (url, formData, { timeoutMs, onUploadProgress }) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.withCredentials = true;
    xhr.timeout = timeoutMs;
    if (typeof onUploadProgress === 'function') {
      xhr.upload.onprogress = onUploadProgress;
    }
    xhr.ontimeout = () => {
      const err = new Error(
        'Upload timed out waiting for the server. Try again or use a smaller file.'
      );
      err.status = 0;
      err.code = 'UPLOAD_TIMEOUT';
      reject(err);
    };
    xhr.onload = () => {
      if (xhr.status === 0) {
        reject(new Error('Network error'));
        return;
      }
      const ct = xhr.getResponseHeader('Content-Type') || '';
      let data;
      try {
        data = ct.includes('application/json') ? JSON.parse(xhr.responseText) : xhr.responseText;
      } catch (parseErr) {
        reject(parseErr);
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        if (digitalMediaUploadClientDebug()) {
          // eslint-disable-next-line no-console
          console.info('[digital-media upload] error response', {
            status: xhr.status,
            code: data && typeof data === 'object' ? data.code : undefined,
            bodyPreview: String(xhr.responseText || '').slice(0, 800),
          });
        }
        const err = new Error(data?.error || data?.message || xhr.statusText);
        err.status = xhr.status;
        err.data = data;
        err.code = data?.code;
        reject(err);
        return;
      }
      resolve(data);
    };
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.send(formData);
  });

const digitalMediaUploadSingle = (formData, onProgress) => {
  const url = browserApiPath('/api/digital-media/upload');
  if (!url) {
    return Promise.reject(new Error('API base URL not available for SSR'));
  }
  if (
    typeof window !== 'undefined' &&
    typeof XMLHttpRequest !== 'undefined' &&
    typeof onProgress === 'function'
  ) {
    return xhrPostFormData(url, formData, {
      timeoutMs: DIGITAL_MEDIA_UPLOAD_XHR_TIMEOUT_MS,
      onUploadProgress: e => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        } else {
          onProgress(-1);
        }
      },
    }).then(data => {
      onProgress(100);
      return data;
    });
  }

  const universalFetch = typeof window !== 'undefined' ? window.fetch : fetch;
  return universalFetch(url, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  }).then(async res => {
    const ct = res.headers.get('Content-Type') || '';
    const data = ct.includes('application/json') ? await res.json() : await res.text();
    if (!res.ok) {
      const err = new Error(data?.error || data?.message || res.statusText);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  });
};

/** Fast.io assembly can exceed Heroku's 30s router limit — poll status until stored. */
const UPLOAD_FINISH_POLL_INTERVAL_MS = 3000;
const UPLOAD_FINISH_MAX_POLLS = 300;

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const pollUploadUntilStored = (finishBody, onProgress) => {
  let attempts = 0;
  const poll = () =>
    jsonFetch('/api/digital-media/upload/status', {
      method: methods.POST,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(finishBody),
    }).then(res => {
      if (res?.data?.status === 'processing') {
        attempts += 1;
        if (attempts >= UPLOAD_FINISH_MAX_POLLS) {
          const err = new Error('Upload timed out waiting for storage to finish processing');
          err.code = 'UPLOAD_FINISH_TIMEOUT';
          throw err;
        }
        if (typeof onProgress === 'function') {
          onProgress(99);
        }
        return sleep(UPLOAD_FINISH_POLL_INTERVAL_MS).then(poll);
      }
      return res;
    });
  return poll();
};

const callUploadFinish = finishBody =>
  jsonFetch('/api/digital-media/upload/finish', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(finishBody),
  });

/** TEMPORARY: posts chunk bytes straight to Fast.io using bearer token from upload/init. */
const uploadChunkToFastio = (directConfig, uploadId, order, blob, fileName) => {
  const size = blob.size;
  const base = String(directConfig.baseUrl || '').replace(/\/$/, '');
  const url = `${base}/current/upload/${encodeURIComponent(
    uploadId
  )}/chunk/?order=${encodeURIComponent(String(order))}&size=${encodeURIComponent(String(size))}`;
  const fd = new FormData();
  fd.append('chunk', blob, fileName);
  return fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${directConfig.bearerToken}`,
    },
    body: fd,
  })
    .then(res => {
      if (res.ok) {
        return res;
      }
      return res.text().then(text => {
        let message = `Fast.io chunk ${order} failed (${res.status})`;
        try {
          const body = JSON.parse(text);
          message = body?.error?.text || body?.message || message;
        } catch (_) {
          if (text) {
            message = text.slice(0, 200);
          }
        }
        const err = new Error(message);
        err.status = res.status;
        throw err;
      });
    })
    .catch(err => {
      if (err?.status != null) {
        throw err;
      }
      const networkErr = new Error('Direct upload to storage failed');
      networkErr.code = 'FASTIO_DIRECT_UPLOAD_NETWORK';
      networkErr.cause = err;
      throw networkErr;
    });
};

const digitalMediaUploadChunked = (payload, onProgress) => {
  const { file, listingId, section, title, accessLimit, durationSeconds } = payload;

  const chunkUrl = browserApiPath('/api/digital-media/upload/chunk');
  const chunkSliceUrl = browserApiPath('/api/digital-media/upload/chunk-slice');
  if (!chunkUrl) {
    return Promise.reject(new Error('API base URL not available for SSR'));
  }

  const accessLimitBody = accessLimit;

  return jsonFetch('/api/digital-media/upload/init', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      listingId,
      section,
      filename: file.name,
      size: file.size,
      mimeType: file.type || 'application/octet-stream',
      title: title || file.name,
    }),
  }).then(initRes => {
    const init = initRes?.data;
    if (!init?.uploadId) {
      throw new Error('Upload init did not return session id');
    }
    const {
      uploadId,
      chunkSizeBytes,
      totalChunks,
      maxParallelChunks = 3,
      directChunkUpload,
      proxySliceBytes,
    } = init;
    const parallel = Math.max(1, Math.min(maxParallelChunks, totalChunks));
    const useDirectFastio = !!directChunkUpload?.bearerToken && !!directChunkUpload?.baseUrl;
    const transport = {
      mode: useDirectFastio ? 'direct' : proxySliceBytes ? 'slice' : 'proxy',
      proxySliceBytes: proxySliceBytes || null,
    };

    const inflightLoaded = new Array(totalChunks).fill(0);
    let completedBytes = 0;

    const reportProgress = () => {
      if (typeof onProgress !== 'function') {
        return;
      }
      const inflight = inflightLoaded.reduce((sum, n) => sum + n, 0);
      const pct = Math.round(((completedBytes + inflight) / file.size) * 98);
      onProgress(Math.min(98, Math.max(0, pct)));
    };

    const postChunkViaProxy = (order, blob, idx) => {
      const fd = new FormData();
      fd.append('uploadId', uploadId);
      fd.append('order', String(order));
      fd.append('chunk', blob, file.name);

      return xhrPostFormData(chunkUrl, fd, {
        timeoutMs: DIGITAL_MEDIA_CHUNK_XHR_TIMEOUT_MS,
        onUploadProgress: e => {
          if (e.lengthComputable) {
            inflightLoaded[idx] = e.loaded;
            reportProgress();
          }
        },
      }).then(() => {
        completedBytes += blob.size;
        inflightLoaded[idx] = 0;
        reportProgress();
      });
    };

    const uploadChunkViaSlices = (order, idx) => {
      if (!chunkSliceUrl) {
        return Promise.reject(new Error('API base URL not available for chunk slices'));
      }
      const start = (order - 1) * chunkSizeBytes;
      const end = Math.min(start + chunkSizeBytes, file.size);
      const chunkTotal = end - start;
      const sliceBytes = transport.proxySliceBytes;
      let offset = 0;

      const uploadNextSlice = () => {
        if (offset >= chunkTotal) {
          return Promise.resolve();
        }
        const sliceEnd = Math.min(offset + sliceBytes, chunkTotal);
        const blob = file.slice(start + offset, start + sliceEnd);
        const sliceLen = sliceEnd - offset;
        offset = sliceEnd;

        const fd = new FormData();
        fd.append('uploadId', uploadId);
        fd.append('order', String(order));
        fd.append('chunk', blob, file.name);

        inflightLoaded[idx] = offset;
        reportProgress();

        return xhrPostFormData(chunkSliceUrl, fd, {
          timeoutMs: DIGITAL_MEDIA_CHUNK_XHR_TIMEOUT_MS,
          onUploadProgress: e => {
            if (e.lengthComputable) {
              inflightLoaded[idx] = offset - sliceLen + e.loaded;
              reportProgress();
            }
          },
        }).then(uploadNextSlice);
      };

      return uploadNextSlice().then(() => {
        completedBytes += chunkTotal;
        inflightLoaded[idx] = 0;
        reportProgress();
      });
    };

    const uploadOneChunk = order => {
      const start = (order - 1) * chunkSizeBytes;
      const end = Math.min(start + chunkSizeBytes, file.size);
      const blob = file.slice(start, end);
      const idx = order - 1;

      if (transport.mode === 'direct') {
        inflightLoaded[idx] = end - start;
        reportProgress();
        return uploadChunkToFastio(directChunkUpload, uploadId, order, blob, file.name)
          .then(() => {
            completedBytes += end - start;
            inflightLoaded[idx] = 0;
            reportProgress();
          })
          .catch(err => {
            inflightLoaded[idx] = 0;
            if (err?.code === 'FASTIO_DIRECT_UPLOAD_NETWORK') {
              transport.mode = transport.proxySliceBytes ? 'slice' : 'proxy';
              if (transport.mode === 'slice') {
                return uploadChunkViaSlices(order, idx);
              }
              return postChunkViaProxy(order, blob, idx);
            }
            throw err;
          });
      }

      if (transport.mode === 'slice') {
        return uploadChunkViaSlices(order, idx);
      }

      return postChunkViaProxy(order, blob, idx);
    };

    let nextOrder = 1;
    const takeOrder = () => {
      if (nextOrder > totalChunks) {
        return null;
      }
      const order = nextOrder;
      nextOrder += 1;
      return order;
    };

    const worker = () => {
      const run = () => {
        const order = takeOrder();
        if (order === null) {
          return Promise.resolve();
        }
        return uploadOneChunk(order).then(run);
      };
      return run();
    };

    const workers = Array.from({ length: parallel }, () => worker());
    return Promise.all(workers).then(() => {
      if (typeof onProgress === 'function') {
        onProgress(99);
      }
      const finishBody = {
        uploadId,
        listingId,
        section,
        title: title || file.name,
        accessLimit: accessLimitBody,
        durationSeconds:
          durationSeconds != null && durationSeconds !== '' ? durationSeconds : undefined,
        stageOnly: payload.stageOnly ? true : undefined,
      };
      return callUploadFinish(finishBody).then(finishRes => {
        const done =
          finishRes?.data?.status === 'processing'
            ? pollUploadUntilStored(finishBody, onProgress)
            : Promise.resolve(finishRes);
        return done.then(finalRes => {
          if (typeof onProgress === 'function') {
            onProgress(100);
          }
          return finalRes;
        });
      });
    });
  });
};

/**
 * Upload digital media to Soulwise → Fast.io.
 * Accepts `{ file, listingId, section, title?, accessLimit?, durationSeconds? }` or legacy FormData.
 * Files larger than 4 MiB use client-side chunking (Heroku-safe).
 */
export const digitalMediaUpload = (payload, onProgress) => {
  if (payload instanceof FormData) {
    return digitalMediaUploadSingle(payload, onProgress);
  }

  const file = payload?.file;
  if (!file) {
    return Promise.reject(new Error('file is required'));
  }

  if (file.size <= FASTIO_SINGLE_CALL_MAX_BYTES) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('listingId', payload.listingId);
    fd.append('section', payload.section);
    fd.append('title', payload.title || file.name);
    if (payload.accessLimit != null) {
      fd.append(
        'accessLimit',
        typeof payload.accessLimit === 'string'
          ? payload.accessLimit
          : JSON.stringify(payload.accessLimit)
      );
    }
    if (payload.durationSeconds != null && payload.durationSeconds !== '') {
      fd.append('durationSeconds', String(payload.durationSeconds));
    }
    if (payload.stageOnly) {
      fd.append('stageOnly', 'true');
    }
    return digitalMediaUploadSingle(fd, onProgress);
  }

  return digitalMediaUploadChunked(payload, onProgress);
};

export const digitalMediaDelete = (listingId, mediaId, fastNodeId) => {
  const base = `/api/digital-media/${encodeURIComponent(listingId)}/${encodeURIComponent(
    digitalMediaIdStr(mediaId)
  )}`;
  const url =
    fastNodeId != null && fastNodeId !== ''
      ? `${base}?fastNodeId=${encodeURIComponent(String(fastNodeId))}`
      : base;
  return jsonFetch(url, { method: 'DELETE' });
};

export const digitalMediaAddLink = payload =>
  jsonFetch('/api/digital-media/link', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...payload,
      stageOnly: payload?.stageOnly ? true : undefined,
    }),
  });

export const digitalMediaUpdateLink = payload =>
  jsonFetch('/api/digital-media/link', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

const unwrapJsonData = payload =>
  payload && typeof payload === 'object' && payload.data != null ? payload.data : payload;

export const createContentSubscription = body =>
  jsonFetch('/api/content-subscriptions', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(unwrapJsonData);

export const confirmContentSubscription = body =>
  jsonFetch('/api/content-subscriptions/confirm', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(unwrapJsonData);

export const createContentSubscriptionPortal = body =>
  jsonFetch('/api/content-subscriptions/portal', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(unwrapJsonData);

export const notifyContentSubscriptionListingClosed = body =>
  jsonFetch('/api/content-subscriptions/listing-closed', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).then(unwrapJsonData);

export const fetchListingTrashEligibility = listingId => {
  const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
  return jsonFetch(`/api/listings/trash-eligibility?listingId=${encodeURIComponent(id || '')}`, {
    method: methods.GET,
  }).then(unwrapJsonData);
};

export const fetchOnlineMeeting = transactionId => {
  const id = typeof transactionId === 'string' ? transactionId : transactionId?.uuid;
  return jsonFetch(`/api/online-meeting?transactionId=${encodeURIComponent(id || '')}`, {
    method: methods.GET,
  }).then(unwrapJsonData);
};

export const copyOwnListing = (listingId, { locale } = {}) => {
  const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
  return jsonFetch('/api/listings/copy', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ listingId: id, locale }),
  }).then(unwrapJsonData);
};

export const fetchContentSubscriptionForTransaction = transactionId => {
  const id = typeof transactionId === 'string' ? transactionId : transactionId?.uuid;
  return jsonFetch(
    `/api/content-subscriptions/for-transaction?transactionId=${encodeURIComponent(id || '')}`,
    { method: methods.GET }
  ).then(unwrapJsonData);
};

export const fetchContentLibrary = () => jsonFetch('/api/content-library', { method: methods.GET });

export const fetchListingContentAccess = listingId => {
  const id = typeof listingId === 'string' ? listingId : listingId?.uuid;
  return jsonFetch(`/api/content-library/access?listingId=${encodeURIComponent(id || '')}`, {
    method: methods.GET,
  });
};

export const fetchDigitalMediaPlayback = (transactionId, mediaId) =>
  jsonFetch(
    `/api/digital-media/playback?transactionId=${encodeURIComponent(
      transactionId
    )}&mediaId=${encodeURIComponent(digitalMediaIdStr(mediaId))}`,
    { method: methods.GET }
  );

export const fetchDigitalMediaTeaserPlayback = (listingId, mediaId, fastNodeId) => {
  const q = new URLSearchParams({
    listingId: typeof listingId === 'string' ? listingId : listingId?.uuid,
    mediaId: digitalMediaIdStr(mediaId),
  });
  if (fastNodeId) {
    q.set('fastNodeId', String(fastNodeId));
  }
  return jsonFetch(`/api/digital-media/teaser-playback?${q.toString()}`, { method: methods.GET });
};

export const fetchDigitalMediaThumbnail = (transactionId, mediaId, fastNodeId) =>
  jsonFetch(
    `/api/digital-media/thumbnail?transactionId=${encodeURIComponent(
      transactionId
    )}&mediaId=${encodeURIComponent(digitalMediaIdStr(mediaId))}&fastNodeId=${encodeURIComponent(
      String(fastNodeId)
    )}`,
    { method: methods.GET }
  );

/** Listing author — thumbnail for edit listing media panel (no transaction). */
export const fetchListingMediaThumbnail = (listingId, mediaId, fastNodeId) =>
  jsonFetch(
    `/api/digital-media/listing-thumbnail?listingId=${encodeURIComponent(
      listingId
    )}&mediaId=${encodeURIComponent(digitalMediaIdStr(mediaId))}&fastNodeId=${encodeURIComponent(
      String(fastNodeId)
    )}`,
    { method: methods.GET }
  );

const appendEditorMediaHints = (q, hints = {}) => {
  const { mimeType, detectedType, title } = hints;
  if (mimeType) {
    q.set('mimeType', String(mimeType));
  }
  if (detectedType) {
    q.set('detectedType', String(detectedType));
  }
  if (title) {
    q.set('title', String(title));
  }
};

/** Listing author — full-file read URL for preview modal in edit listing flow. */
export const fetchEditorMediaPlayback = (listingId, mediaId, fastNodeId, hints = {}) => {
  const q = new URLSearchParams({
    listingId: String(listingId),
    mediaId: digitalMediaIdStr(mediaId),
  });
  if (fastNodeId) {
    q.set('fastNodeId', String(fastNodeId));
  }
  appendEditorMediaHints(q, hints);
  return jsonFetch(`/api/digital-media/editor-playback?${q.toString()}`, { method: methods.GET });
};

/** Same-origin paths for iframe `src` (session cookies); server streams with Content-Disposition: inline. */
export const digitalMediaLibraryInlinePath = (transactionId, mediaId) => {
  const q = new URLSearchParams({
    transactionId: String(transactionId),
    mediaId: digitalMediaIdStr(mediaId),
  });
  return `/api/digital-media/library-inline?${q.toString()}`;
};

export const digitalMediaLibraryCardPreviewPath = (transactionId, mediaId, coverFastNodeId) => {
  const q = new URLSearchParams({
    transactionId: String(transactionId),
    mediaId: digitalMediaIdStr(mediaId),
  });
  if (coverFastNodeId) {
    q.set('coverFastNodeId', String(coverFastNodeId));
  }
  return `/api/digital-media/library-card-preview?${q.toString()}`;
};

export const digitalMediaLibraryCoverPreviewPath = (transactionId, mediaId, coverFastNodeId) =>
  digitalMediaLibraryCardPreviewPath(transactionId, mediaId, coverFastNodeId);

export const digitalMediaEditorInlinePath = (listingId, mediaId, fastNodeId, hints = {}) => {
  const q = new URLSearchParams({
    listingId: String(listingId),
    mediaId: digitalMediaIdStr(mediaId),
    fastNodeId: String(fastNodeId),
  });
  appendEditorMediaHints(q, hints);
  return `/api/digital-media/editor-inline?${q.toString()}`;
};

export const digitalMediaEditorCardPreviewPath = (
  listingId,
  mediaId,
  fastNodeId,
  coverFastNodeId,
  hints = {}
) => {
  const q = new URLSearchParams({
    listingId: String(listingId),
    mediaId: digitalMediaIdStr(mediaId),
    fastNodeId: String(fastNodeId),
  });
  if (coverFastNodeId) {
    q.set('coverFastNodeId', String(coverFastNodeId));
  }
  appendEditorMediaHints(q, hints);
  return `/api/digital-media/editor-card-preview?${q.toString()}`;
};

export const digitalMediaEditorCoverPreviewPath = (listingId, mediaId, coverFastNodeId) => {
  const q = new URLSearchParams({
    listingId: String(listingId),
    mediaId: digitalMediaIdStr(mediaId),
  });
  if (coverFastNodeId) {
    q.set('coverFastNodeId', String(coverFastNodeId));
  }
  return `/api/digital-media/editor-card-preview?${q.toString()}`;
};

export const digitalMediaTeaserInlinePath = (listingId, mediaId, fastNodeId) => {
  const q = new URLSearchParams({
    listingId: typeof listingId === 'string' ? listingId : listingId?.uuid,
    mediaId: digitalMediaIdStr(mediaId),
  });
  if (fastNodeId) {
    q.set('fastNodeId', String(fastNodeId));
  }
  return `/api/digital-media/teaser-inline?${q.toString()}`;
};

export const digitalMediaTeaserCardPreviewPath = (
  listingId,
  mediaId,
  fastNodeId,
  coverFastNodeId
) => {
  const q = new URLSearchParams({
    listingId: typeof listingId === 'string' ? listingId : listingId?.uuid,
    mediaId: digitalMediaIdStr(mediaId),
  });
  if (fastNodeId) {
    q.set('fastNodeId', String(fastNodeId));
  }
  if (coverFastNodeId) {
    q.set('coverFastNodeId', String(coverFastNodeId));
  }
  return `/api/digital-media/teaser-card-preview?${q.toString()}`;
};

export const digitalMediaTeaserCoverPreviewPath = (listingId, mediaId, coverFastNodeId) => {
  const q = new URLSearchParams({
    listingId: typeof listingId === 'string' ? listingId : listingId?.uuid,
    mediaId: digitalMediaIdStr(mediaId),
  });
  if (coverFastNodeId) {
    q.set('coverFastNodeId', String(coverFastNodeId));
  }
  return `/api/digital-media/teaser-card-preview?${q.toString()}`;
};

export const digitalMediaCoverUpload = (payload, onProgress) => {
  const { file, listingId, mediaId } = payload || {};
  if (!file || !listingId || !mediaId) {
    return Promise.reject(new Error('file, listingId, and mediaId are required'));
  }
  const fd = new FormData();
  fd.append('file', file);
  fd.append('listingId', listingId);
  fd.append('mediaId', digitalMediaIdStr(mediaId));
  const url = browserApiPath('/api/digital-media/cover');
  if (!url) {
    return Promise.reject(new Error('API base URL not available for SSR'));
  }
  if (
    typeof window !== 'undefined' &&
    typeof XMLHttpRequest !== 'undefined' &&
    typeof onProgress === 'function'
  ) {
    return xhrPostFormData(url, fd, {
      timeoutMs: DIGITAL_MEDIA_UPLOAD_XHR_TIMEOUT_MS,
      onUploadProgress: e => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        } else {
          onProgress(-1);
        }
      },
    }).then(data => {
      onProgress(100);
      return data;
    });
  }
  const universalFetch = typeof window !== 'undefined' ? window.fetch : fetch;
  return universalFetch(url, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  }).then(async res => {
    const ct = res.headers.get('Content-Type') || '';
    const data = ct.includes('application/json') ? await res.json() : await res.text();
    if (!res.ok) {
      const err = new Error(data?.error || data?.message || res.statusText);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  });
};

export const digitalMediaCoverDelete = ({ listingId, mediaId, coverFastNodeId }) => {
  const q = new URLSearchParams({
    listingId: String(listingId),
    mediaId: digitalMediaIdStr(mediaId),
  });
  if (coverFastNodeId != null && coverFastNodeId !== '') {
    q.set('coverFastNodeId', String(coverFastNodeId));
  }
  return jsonFetch(`/api/digital-media/cover?${q.toString()}`, { method: methods.DELETE });
};

export const fetchDigitalMediaPurchaserCount = listingId =>
  jsonFetch(
    `/api/digital-media/purchaser-count?listingId=${encodeURIComponent(String(listingId))}`,
    { method: methods.GET }
  );

export const notifyDigitalMediaPurchasers = ({ listingId, message }) =>
  jsonFetch('/api/digital-media/notify-purchasers', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ listingId, message }),
  });

export const createShortLinkApi = ({ pathname, search }) =>
  jsonFetch('/api/short-links', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pathname, search: search || '' }),
  });

export const fetchUserIdByHandleApi = handle =>
  jsonFetch(`/api/profile-handles/${encodeURIComponent(handle)}`, { method: methods.GET });

export const fetchCurrentUserHandleApi = () =>
  jsonFetch('/api/profile-handles/me', { method: methods.GET });

export const fetchHandleByUserIdApi = userId =>
  jsonFetch(`/api/profile-handles/user/${encodeURIComponent(userId)}`, { method: methods.GET });

export const fetchProfileHandleAvailabilityApi = handle =>
  jsonFetch(`/api/profile-handles/${encodeURIComponent(handle)}/available`, {
    method: methods.GET,
  });

export const claimProfileHandleApi = handle =>
  jsonFetch('/api/profile-handles', {
    method: methods.POST,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handle }),
  });

export const releaseProfileHandleApi = () =>
  jsonFetch('/api/profile-handles', { method: methods.DELETE });
